import { useCallback, useEffect, useState } from 'react';

import { CallStatus } from '../../../../../shared/providers/PhoneCallContext';

export const useRecordingDisplayActions = ({
    status,
    isRecording,
}: {
    status: CallStatus;
    isRecording: boolean;
}) => {
    const TIME_TO_PROMPT_RECORDING = 10000;
    const DURATION_TO_SHOW_RECORDING = 10000;
    const TIME_TO_SHOW_PULSE = 60000;

    const [showRecordConfirmModal, setShowRecordConfirmModal] = useState(false);
    const [hasPromptedRecording, setHasPromptedRecording] = useState(false);
    const [shouldShowPulse, setShouldShowPulse] = useState(false);

    // Auto-show recording modal after TIME_TO_PROMPT_RECORDING when call is active
    useEffect(() => {
        if (status === 'active' && !isRecording && !hasPromptedRecording) {
            const timer = window.setTimeout(() => {
                setShowRecordConfirmModal(true);
                setHasPromptedRecording(true);
            }, TIME_TO_PROMPT_RECORDING);

            return () => {
                window.clearTimeout(timer);
            };
        }
    }, [status, isRecording, hasPromptedRecording]);

    // Auto-close recording modal after DURATION_TO_SHOW_RECORDING
    useEffect(() => {
        if (!showRecordConfirmModal) return undefined;

        const autoCloseTimer = window.setTimeout(() => {
            setShowRecordConfirmModal(false);
        }, DURATION_TO_SHOW_RECORDING);

        return () => {
            window.clearTimeout(autoCloseTimer);
        };
    }, [showRecordConfirmModal]);

    const onConfirmRecording = useCallback(() => {
        setShowRecordConfirmModal(false);
    }, []);

    const onRejectRecording = useCallback(() => {
        setShowRecordConfirmModal(false);
    }, []);

    const onButtonHover = useCallback(() => {
        setShouldShowPulse(false);
    }, []);

    // Show pulse animation after 60 seconds if not recording
    useEffect(() => {
        if (status === 'active' && !isRecording) {
            const pulseTimer = window.setTimeout(() => {
                setShouldShowPulse(true);
            }, TIME_TO_SHOW_PULSE);

            return () => {
                window.clearTimeout(pulseTimer);
            };
        }

        // Clear pulse when recording starts or call is no longer active
        if (isRecording || status !== 'active') {
            setShouldShowPulse(false);
        }
    }, [status, isRecording]);

    return {
        showRecordConfirmModal,
        shouldShowPulse,
        onConfirmRecording,
        onRejectRecording,
        onButtonHover,
    };
};
